// packages/web/src/pages/main/StudentAssignmentsPage.tsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { userAssignmentApi } from '../../services/userAssignmentApi';
import { MathRenderer } from '../../components/common/MathRenderer.v3';

interface Assignment {
  id: string;
  title: string;
  initial_question: string;
  category: string;
  teacher_requirement: string;
  status: 'pending' | 'active' | 'completed';
  assigned_at: string;
  image_url: string;
  teacher_name: string;
}

export const StudentAssignmentsPage: React.FC = () => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<'all' | 'pending' | 'completed'>('all');
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadAssignments();
  }, [filter]);

  const loadAssignments = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await userAssignmentApi.getAssignments(filter);
      
      if (response.success && response.data) {
        setAssignments(response.data.assignments);
      } else {
        throw new Error(response.error?.message || '加载失败');
      }
    } catch (err: any) {
      setError(err.message || '加载作业列表失败');
    } finally {
      setLoading(false);
    }
  };

  const handleStartAssignment = (assignmentId: string) => {
    // 跳转到对话页面，使用 state 传递 topicId（与历史记录页面保持一致）
    navigate('/chat', {
      state: {
        loadHistoryTopicId: assignmentId,
        fromHistory: true
      }
    });
  };

  const getStatusBadge = (status: string) => {
    const badges = {
      pending: { text: '待完成', className: 'bg-yellow-100 text-yellow-800' },
      active: { text: '进行中', className: 'bg-blue-100 text-blue-800' },
      completed: { text: '已完成', className: 'bg-green-100 text-green-800' },
    };
    const badge = badges[status as keyof typeof badges] || badges.pending;
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium ${badge.className}`}>
        {badge.text}
      </span>
    );
  };

  return (
    <div className="student-assignments-page min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">我的作业</h1>

        {/* 筛选标签 */}
        <div className="mb-6 flex gap-4">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              filter === 'all'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            全部
          </button>
          <button
            onClick={() => setFilter('pending')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              filter === 'pending'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            待完成
          </button>
          <button
            onClick={() => setFilter('completed')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              filter === 'completed'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            已完成
          </button>
        </div>

        {/* 错误提示 */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-800">{error}</p>
            <button
              onClick={loadAssignments}
              className="mt-2 text-sm text-red-600 hover:text-red-800"
            >
              重试
            </button>
          </div>
        )}

        {/* 加载状态 */}
        {loading && (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            <p className="mt-4 text-gray-600">加载中...</p>
          </div>
        )}

        {/* 空状态 */}
        {!loading && assignments.length === 0 && (
          <div className="text-center py-12 bg-white rounded-lg shadow">
            <div className="text-6xl mb-4">📚</div>
            <p className="text-xl text-gray-600 mb-2">暂无作业</p>
            <p className="text-gray-500">老师还没有布置作业</p>
          </div>
        )}

        {/* 作业列表 */}
        {!loading && assignments.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {assignments.map((assignment) => (
              <div
                key={assignment.id}
                className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow overflow-hidden flex flex-col"
              >
                <div className="p-6 flex-1 flex flex-col">
                  {/* 状态标签和科目 */}
                  <div className="flex items-center justify-between mb-3">
                    {getStatusBadge(assignment.status)}
                    <span className="text-xs text-gray-500">
                      {assignment.category === 'math' ? '数学' : 
                       assignment.category === 'physics' ? '物理' : 
                       assignment.category === 'chemistry' ? '化学' : '其他'}
                    </span>
                  </div>

                  {/* 题目标题 */}
                  <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">
                    {assignment.title}
                  </h3>

                  {/* 题目描述 */}
                  <div className="text-sm text-gray-600 mb-3 line-clamp-3 flex-1">
                    <MathRenderer content={assignment.initial_question} />
                  </div>

                  {/* 教学要求 */}
                  {assignment.teacher_requirement && (
                    <div className="mb-3 p-3 bg-blue-50 rounded-lg">
                      <p className="text-xs text-gray-500 mb-1">教学要求</p>
                      <p className="text-sm text-blue-800 line-clamp-2">
                        {assignment.teacher_requirement}
                      </p>
                    </div>
                  )}

                  {/* 元信息 */}
                  <div className="space-y-2 mb-4 text-xs text-gray-500">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">👨‍🏫 老师：</span>
                      <span>{assignment.teacher_name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">📅 截止：</span>
                      <span>{new Date(assignment.assigned_at).toLocaleDateString('zh-CN')}</span>
                    </div>
                  </div>

                  {/* 操作按钮 */}
                  <button
                    onClick={() => handleStartAssignment(assignment.id)}
                    className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                  >
                    {assignment.status === 'completed' ? '查看作业' : assignment.status === 'active' ? '继续作业' : '开始作业'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
